import { useNavigate } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { AuthContext } from "src/utils/AuthContext.js";
import { LoadingButton } from "../../blocks/LoadingButton.js";
import { Typography, Box, Icon, Button } from "@mui/material";
import { getPageRoute } from "src/services";
import { UpgradeConsumerButton } from "../../upgrades/consumer/UpgradeConsumerButton.js";
import TransparentUpdatesExplainer from "src/components/explainers/TransparentUpdatesExplainer.js";
import InstructionsLayout from "../InstructionsLayout.js";
import { useTheme } from "@emotion/react";
import OpenCalendarDialog from "../../blocks/dialogs/OpenCalendarDialog.js";
import { useSubscribe } from "src/utils/useSubscribe.js";
import { customEvent } from "src/utils/gtag.js";
import HowTrialWorks from "../../upgrades/consumer/HowTrialWorks.js";
import useGetCalendarFromParams from "src/utils/useGetCalendarFromParams.js";
import { Features, hasAccess } from "src/services/FeatureGatingService.js";

const CalendarSteps = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { calendar, isLoadingCalendar, isCalendarError } =
    useGetCalendarFromParams();
  const theme = useTheme();
  const [openCalendarDialog, setOpenCalendarDialog] = useState(false);
  const { subscribe } = useSubscribe();

  useEffect(() => {
    customEvent({
      name: "landed_on_other_calendar_steps",
      category: "OtherCalendarSteps",
      label: "Landed on Other Calendar Steps",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });
  }, []);

  return (
    <InstructionsLayout nav="back">
      {/* TransparentUpdatesExplainer Animation */}
      <TransparentUpdatesExplainer
        onUpgradeClick={() => {
          if (hasAccess(user, Features.CONNECT_OTHER_CALENDAR.key)) {
            navigate(
              getPageRoute("help", "CALENDAR_CONNECTED", {
                handle: calendar.handle,
                calendarId: calendar._id,
              })
            );
          }
          // If user doesn't have access, UpgradeConsumerButton below will handle it
        }}
        onSeeAllBenefits={() => {
          // Could navigate to benefits page if needed
        }}
        sx={{ mb: 4 }}
      />

      {!hasAccess(user, Features.CONNECT_OTHER_CALENDAR.key) ? (
        <>
          <UpgradeConsumerButton
            size="grown"
            variant="contained"
            color="primary"
            sx={{ width: "100%", mt: 2 }}
            state={{ calendar, source: "other_calendar_steps" }}
            successUrl={getPageRoute("help", "CALENDAR_CONNECTED", {
              handle: calendar?.handle,
              calendarId: calendar?._id,
            })}
            data-upgrade-button="other-calendar-steps"
          >
            GET 7 DAYS FREE
          </UpgradeConsumerButton>

          <HowTrialWorks calendar={calendar} sx={{ mt: 2 }} />

          <Button
            variant="outlined"
            size="grown"
            sx={{ mt: 2, mb: 2, width: "100%" }}
            onClick={() => {
              customEvent({
                name: "user_click_status_quo_other_steps",
                category: "OtherCalendarSteps",
                label: "Clicked Go Back to Status Quo",
                data: {
                  user_id: user?._id,
                  user_email: user?.email,
                  calendar_handle: calendar?.handle,
                },
              });
              subscribe({ calendar });
              setOpenCalendarDialog(true);
            }}
          >
            Go back to Status Quo
          </Button>
        </>
      ) : (
        <>
          <Typography
            variant={"subtitle2"}
            sx={{ textAlign: "left", mr: "auto", mt: 1 }}
            color="text.secondary"
          >
            Members save time and are the first to know.
          </Typography>
          <LoadingButton
            size="grown"
            variant="contained"
            color="primary"
            sx={{ mt: 2, height: "auto", width: "100%" }}
            onClick={() => {
              navigate(
                getPageRoute("help", "CALENDAR_CONNECTED", {
                  handle: calendar.handle,
                  calendarId: calendar._id,
                })
              );
            }}
          >
            Get Started
          </LoadingButton>
        </>
      )}
      <OpenCalendarDialog
        open={openCalendarDialog}
        handleClose={() => setOpenCalendarDialog(false)}
        calendar={calendar}
      />
    </InstructionsLayout>
  );
};

export default CalendarSteps;
